import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Card } from '@/components';
import { Button, Icon, IconButton, LoadingIcon, Modal, Typography } from '@/primer/components';
import type { IconName } from '@/primer/components';
import { PageColumn } from '@/components/layout';

interface ConnectSocialPageProps {
  providerName: string;
  icon: IconName;
  isConnected: boolean;
  username?: string;
  isLoadingConnection: boolean;
  isConnecting: boolean;
  isDisconnecting: boolean;
  error: string;
  onConnect: () => void;
  onDisconnect: () => Promise<void>;
  /** Back-header target. Defaults to Settings. */
  onBack?: () => void;
  /** Back-header label. Defaults to "Settings". */
  backLabel?: string;
  /** Bottom button target. Defaults to the dashboard. */
  onContinue?: () => void;
  /** Bottom button label when connected. */
  continueLabel?: string;
  /**
   * Hard gate: when true, the bottom button is disabled until connected (no
   * "Skip for now"). Defaults to false (the settings/onboarding behavior).
   */
  requireConnection?: boolean;
}

export function ConnectSocialPage({
  providerName,
  icon,
  isConnected,
  username,
  isLoadingConnection,
  isConnecting,
  isDisconnecting,
  error,
  onConnect,
  onDisconnect,
  onBack,
  backLabel = 'Settings',
  onContinue,
  continueLabel,
  requireConnection = false,
}: ConnectSocialPageProps) {
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const navigate = useNavigate();

  const handleBack = onBack ?? (() => navigate({ to: '/settings' }));
  const handleContinue =
    onContinue ?? (() => navigate({ to: '/dashboard', search: { tab: 'active' } }));
  const bottomLabel = requireConnection
    ? (continueLabel ?? 'Continue')
    : isConnected
      ? (continueLabel ?? 'Continue to Dashboard')
      : 'Skip for now';

  const handleDisconnectConfirm = async () => {
    if (isDisconnecting) return;
    try {
      await onDisconnect();
    } catch (err) {
      // Surfaced to the user via the `error` prop; caught here so the rejected
      // mutation promise doesn't escape as an unhandled rejection.
      console.error('Failed to disconnect social provider:', err);
    } finally {
      setShowDisconnectModal(false);
    }
  };

  if (isLoadingConnection) {
    return (
      <div className="flex justify-center py-8">
        <LoadingIcon size="large" />
      </div>
    );
  }

  return (
    <PageColumn className="py-8 space-y-6">
      <div className="md:hidden flex items-center gap-4">
        <IconButton icon="chevron-left" onClick={handleBack} aria-label={`Back to ${backLabel}`} />
        <Typography variant="header" size="h3" as="h3">
          {backLabel}
        </Typography>
      </div>

      {error && (
        <div className="bg-error border border-error rounded-small p-3">
          <Typography variant="body" size="small" color="error">
            {error}
          </Typography>
        </div>
      )}

      <Card>
        <div className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Icon name={icon} size="large" />
              <div>
                <Typography variant="header" size="h3" as="h3">
                  {providerName}
                </Typography>
                <Typography variant="body" size="small" color="bodylight">
                  {isConnected
                    ? username
                      ? `@${username}`
                      : 'Your account is linked'
                    : 'Not connected'}
                </Typography>
              </div>
            </div>
            {isConnected ? (
              <Button
                size="small"
                variant="secondary"
                onClick={() => setShowDisconnectModal(true)}
                isLoading={isDisconnecting}
              >
                Disconnect
              </Button>
            ) : (
              <Button size="small" onClick={onConnect} isLoading={isConnecting}>
                Connect
              </Button>
            )}
          </div>
        </div>
      </Card>

      <Button fullWidth onClick={handleContinue} disabled={requireConnection && !isConnected}>
        {bottomLabel}
      </Button>

      {!isConnected && (
        <Typography variant="body" size="small" color="bodylight" align="center">
          We only access your public profile information. We never post on your behalf.
        </Typography>
      )}

      <Modal
        isOpen={showDisconnectModal}
        onClose={() => {
          if (!isDisconnecting) setShowDisconnectModal(false);
        }}
        icon={icon}
        title={`Disconnect ${providerName}`}
        description={`Are you sure you want to disconnect your ${providerName} profile from Influenster?`}
        primaryButton={{
          label: isDisconnecting ? 'Disconnecting…' : `Yes, Disconnect ${providerName}`,
          onClick: handleDisconnectConfirm,
        }}
        secondaryButton={{
          label: 'Cancel',
          onClick: () => {
            if (!isDisconnecting) setShowDisconnectModal(false);
          },
        }}
      />
    </PageColumn>
  );
}
